import React, { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { resolveSceneLook, toneMappingForLook } from '../../Utils/sceneLooks';

function CameraHeadlight({ intensity = 0, color = '#ffffff' }) {
  const lightRef = useRef();
  const camera = useThree((s) => s.camera);
  const scene = useThree((s) => s.scene);

  useEffect(() => {
    const light = lightRef.current;
    if (!light || intensity <= 0) return undefined;
    scene.add(light.target);
    return () => {
      scene.remove(light.target);
    };
  }, [scene, intensity]);

  useFrame(() => {
    const light = lightRef.current;
    if (!light || intensity <= 0) return;
    light.position.copy(camera.position);
    camera.getWorldDirection(light.target.position);
    light.target.position.add(camera.position);
  });

  if (intensity <= 0) return null;
  return <directionalLight ref={lightRef} intensity={intensity} color={color} />;
}

/**
 * Applies a named lighting look: lights, tone mapping, and optional local studio environment
 * (no CDN HDRI — works offline).
 */
export default function SceneLights({ lookId }) {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const look = resolveSceneLook(lookId);

  useEffect(() => {
    gl.toneMapping = toneMappingForLook(look);
    gl.toneMappingExposure = look.exposure ?? 1;
    if ('environmentIntensity' in scene) {
      scene.environmentIntensity = look.roomEnv ? look.envIntensity ?? 0.3 : 0;
    }

    if (!look.roomEnv) {
      scene.environment = null;
      return undefined;
    }

    let envTex = null;
    let pmrem = null;
    try {
      pmrem = new THREE.PMREMGenerator(gl);
      const envScene = new RoomEnvironment();
      envTex = pmrem.fromScene(envScene, 0.04).texture;
      scene.environment = envTex;
      envScene.dispose?.();
    } catch (err) {
      console.warn('[SceneLights] studio environment unavailable', err);
      scene.environment = null;
    }

    return () => {
      if (envTex && scene.environment === envTex) scene.environment = null;
      envTex?.dispose();
      pmrem?.dispose();
    };
  }, [gl, scene, look]);

  const hemi = look.hemisphere;
  const ambient = look.ambient;
  const dirs = look.directional || [];

  return (
    <group key={look.id} name={`SceneLook:${look.id}`}>
      {hemi ? (
        <hemisphereLight
          color={hemi.sky}
          groundColor={hemi.ground}
          intensity={hemi.intensity}
        />
      ) : null}
      {ambient ? <ambientLight color={ambient.color} intensity={ambient.intensity} /> : null}
      {dirs.map((d, i) => (
        <directionalLight
          key={`${look.id}-d${i}`}
          position={d.position}
          intensity={d.intensity}
          color={d.color || '#ffffff'}
        />
      ))}
      <CameraHeadlight intensity={look.headlight || 0} color="#ffffff" />
    </group>
  );
}
